import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link as LinkRouter, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Stack,
  Typography,
  Link,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import ErrorText from '../../../../../theme/components/Forms/ErrorText';
import AuthLayout from '../../../../../components/auth/AuthLayout';
import { registerClient } from '../../../../../redux/auth/actions';
import { getPolyglot } from '../../../../../i18n';
import StoerkIdAPI from '../../../../../redux/stoerkID/StoerkIdAPI';
import {
  ShowErrorMessageProps,
  withShowErrorMessage,
} from '../../../../../components/HOC';
import {
  RouterComponentProps,
  withRouter,
} from '../../../../../util/route-dom';
import TermsAndConditionsDialog from '../../../../../components/StoerkId/StoerkIdRequest/StoerkIdRequestSelectPlan/TermsAndConditionsDialog';
import ValidationUtil from '../../../../../util/ValidationUtil';
import LoadingButton from '@mui/lab/LoadingButton';
import PasswordRequirements from '../../../../../components/auth/PasswordRequirements/PasswordRequirements';
import SC_background from '../../../assets/scc_login_image.jpg';
import TermsAndConditionsContent from '../../../public/TERMS_AND_CONDITIONS_OF_SALE.pdf';
import { useAppSelector } from '../../../../../redux/store.model';
import { getFeatureToggle } from '../../../../../featureToggle';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { InvitationsAPI } from '../../../../../redux/invitations/api/InvitationsAPI';
import {
  Invitation,
  InvitationState,
} from '../../../../../redux/invitations/invitation.model';
import { isInviationValid, isUserActive } from './invitationValidations';
import { UserRegistrationRequest } from '../../../../../redux/stoerkID/StoerkIdAPI.model';
import InvitationRequestPage from './InvitationRequestPage';
import { useShowMessage } from '../../../../../util/hooks';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface FreshwatchRegisterFormProps
  extends ShowErrorMessageProps,
    RouterComponentProps,
    ConnectedComponentProps {}

interface InitialValues {
  name: string;
  email: string;
  workspaceId: string;
  companyName: string;
  password: string;
  passwordConfirm: string;
  termsAndConditions: boolean;
}

const createValidationSchema = (
  invitation: Invitation | undefined,
  invitationId: string | null
) => {
  const isInvitation = invitationId || invitation;
  const validatePassword =
    !isInvitation || (invitation && !isUserActive(invitation));

  return Yup.object().shape({
    name: Yup.string().required('error.formular.name_required'),
    email: Yup.string()
      .email('error.formular.enter_valid_email')
      .required('stoerk_id.share_device.email_required'),
    workspaceId: Yup.string(),
    companyName: Yup.string().required(
      'freshwatch.registration.company_name_required'
    ),
    password: validatePassword
      ? Yup.string()
          .required('error.formular.password_confirmation')
          .test('password-strength', '', function (value) {
            if (!value) return true;
            const { rules } = ValidationUtil.validatePassword(value);
            const errors: string[] = [];
            if (!rules.minLength)
              errors.push('error.formular.password_min_length');
            if (!rules.hasNumber) errors.push('error.formular.password_number');
            if (!rules.hasSymbol) errors.push('error.formular.password_symbol');
            if (!rules.hasLowercase)
              errors.push('error.formular.password_lowercase');
            if (!rules.hasUppercase)
              errors.push('error.formular.password_uppercase');
            if (errors.length > 0) {
              return this.createError({ message: errors.join('|') });
            }
            return true;
          })
      : Yup.string(),
    passwordConfirm: validatePassword
      ? Yup.string()
          .oneOf(
            [Yup.ref('password'), ''],
            'error.formular.password_confirmation'
          )
          .required('error.formular.password_confirmation')
      : Yup.string(),
    termsAndConditions: validatePassword
      ? Yup.boolean().oneOf(
          [true],
          'stoerk_id.request_form.require_agree_terms'
        )
      : Yup.boolean(),
  });
};

const FreshwatchRegisterForm: React.FC<FreshwatchRegisterFormProps> = ({
  registerClient,
  navigate,
  showErrorMessage,
}) => {
  const polyglot = getPolyglot();
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('invitationId');
  const [openModal, setOpenModal] = useState(false);
  const [isRead, setIsRead] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const featureToggle = useAppSelector(getFeatureToggle);
  const workspaceId = featureToggle?.defaultWorkspaceId;
  const [registerUser, { isLoading, reset }] =
    StoerkIdAPI.useRegisterUserRequestMutation();
  const showMessage = useShowMessage();
  const { data: invitation, isLoading: isInvitationLoading } =
    InvitationsAPI.useGetInvitationToRegisterByIdQuery(
      {
        workspaceId: workspaceId ?? '',
        requestId: invitationId ?? '',
      },
      { skip: !invitationId }
    );

  const [acceptsInvitation, { isLoading: isAcceptingInvitation }] =
    InvitationsAPI.useAcceptInvitationMutation();

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
  };
  const handleCloseModalWithConfirmation = () => {
    handleCloseModal();
  };

  const handleAcceptInvitation = useCallback(async () => {
    if (!invitation || !workspaceId) return;
    try {
      await acceptsInvitation({
        workspaceId: invitation.workspaceId,
        requestId: invitation.requestId,
      }).unwrap();
      showMessage(polyglot.t('freshwatch.invitation.approve_success'));

      navigate('/login', { replace: true });
    } catch (error: any) {
      showErrorMessage(error?.message ?? error);
    }
  }, [invitation, workspaceId, acceptsInvitation, navigate, showErrorMessage]);

  const validationSchema = useMemo(
    () => createValidationSchema(invitation, invitationId),
    [invitation, invitationId]
  );

  const isUserAlreadyRegistered = useMemo(
    () => invitation && isInviationValid(invitation) && invitation.userActive,
    [invitation, invitationId]
  );

  const initialValues: InitialValues = {
    name: '',
    email: invitation?.email ?? '',
    workspaceId: workspaceId ?? '',
    companyName: invitation?.companyName ?? '',
    password: '',
    passwordConfirm: '',
    termsAndConditions: false,
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, helpers) => {
      // Reset previous errors
      reset();
      try {
        if (!workspaceId) {
          throw new Error('Workspace ID is not configured');
        }
        const registrationData: UserRegistrationRequest = {
          email: values.email,
          name: values.name,
          workspaceId: values.workspaceId || workspaceId,
          companyName: values.companyName,
          password: values.password,
        };
        if (invitation) {
          const invitationToAccept = {
            workspaceId: invitation.workspaceId,
            requestId: invitation.requestId,
          };
          if (!isUserActive(invitation)) {
            Object.assign(invitationToAccept, registrationData);
          }
          await acceptsInvitation({
            ...invitationToAccept,
          }).unwrap();
        } else {
          await registerUser({
            workspaceId,
            registrationData,
          }).unwrap();
        }
        navigate('/registered', { replace: true });
      } catch (error: any) {
        if (error.type === 'DUPLICATE_USERDATA') {
          showErrorMessage(
            polyglot.t('freshwatch.registration.error_duplicate_user')
          );
        }
        showErrorMessage(error?.message ?? error);
      }
    },
  });

  useEffect(() => {
    formik.setValues((prevValues) => ({
      ...prevValues,
      email: invitation?.email ?? '',
      companyName: invitation?.companyName ?? '',
    }));
  }, [invitation]);

  if (isInvitationLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show InvitationRequestPage for all invitation-related scenarios
  if (
    isUserAlreadyRegistered ||
    invitation?.status === InvitationState.REJECTED ||
    invitation?.status === InvitationState.APPROVED
  ) {
    return (
      <InvitationRequestPage
        invitation={invitation}
        invitationId={invitationId}
        onAccept={handleAcceptInvitation}
        onReject={() => navigate('/login', { replace: true })}
        onBackToLogin={() => navigate('/login', { replace: true })}
        isLoading={isAcceptingInvitation}
      />
    );
  }

  return (
    <AuthLayout imageRight={SC_background}>
      <Box
        sx={{
          margin: 2,
          overflow: 'hidden',
        }}
      >
        {invitation ? (
          <Typography variant="h1" gutterBottom>
            {polyglot.t('freshwatch.invitation.request_title') ||
              'Invitation Request'}
          </Typography>
        ) : (
          <Typography variant="h1">{polyglot.t('register.title')}</Typography>
        )}
        {!invitation || invitation?.userActive ? (
          <Box>
            <Typography component="p">
              {polyglot.t('register.login_link_message')}{' '}
              <Link to="/login" component={LinkRouter}>
                {polyglot.t('register.login_link_title')}
              </Link>
            </Typography>
          </Box>
        ) : null}
        {invitation ? (
          <Box sx={{ marginTop: 2 }}>
            <Typography variant="h5">
              {polyglot.t('freshwatch.invitation.join_company_message')}
            </Typography>
            {invitation.personalMessage && (
              <Box
                sx={{
                  padding: 2,
                  marginTop: 2,
                  backgroundColor: 'background.paper',
                  'padding-inline': '1.5rem',
                  position: 'relative',
                }}
              >
                <Typography
                  variant="body2"
                  component={'blockquote'}
                  sx={{
                    marginX: '1.2rem',
                    ':before': {
                      content: '"“"',
                      fontFamily: 'serif',
                      position: 'absolute',
                      fontSize: '4rem',
                      color: (theme) => theme.palette.primary.main,
                      top: '-1rem',
                      left: '1rem',
                    },
                    ':after': {
                      content: '"”"',
                      fontFamily: 'serif',
                      position: 'absolute',
                      fontSize: '4rem',
                      color: (theme) => theme.palette.primary.main,
                      top: '-1rem',
                      right: '1rem',
                    },
                  }}
                >
                  {invitation.personalMessage}
                </Typography>
              </Box>
            )}
          </Box>
        ) : null}
        <Box sx={{ marginTop: 4 }}>
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
              <TextField
                name="name"
                type="text"
                label={polyglot.t('freshwatch.registration.name')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                required
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={
                  formik.touched.name &&
                  formik.errors.name && (
                    <ErrorText>{polyglot.t(formik.errors.name)}</ErrorText>
                  )
                }
              />
              <TextField
                name="email"
                type="email"
                label={polyglot.t('login.email')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                required
                disabled={Boolean(invitation?.email)}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={
                  formik.touched.email &&
                  formik.errors.email && (
                    <ErrorText>{polyglot.t(formik.errors.email)}</ErrorText>
                  )
                }
              />

              {!invitation ? (
                <TextField
                  name="companyName"
                  type="text"
                  label={polyglot.t('freshwatch.registration.company_name')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.companyName}
                  required
                  error={
                    formik.touched.companyName &&
                    Boolean(formik.errors.companyName)
                  }
                  helperText={
                    formik.touched.companyName &&
                    formik.errors.companyName && (
                      <ErrorText>
                        {polyglot.t(formik.errors.companyName)}
                      </ErrorText>
                    )
                  }
                />
              ) : null}
              {!isUserAlreadyRegistered ? (
                <>
                  {' '}
                  <Box>
                    <TextField
                      name="password"
                      type="password"
                      label={polyglot.t('login.password')}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      required
                      fullWidth
                      error={
                        formik.touched.password &&
                        Boolean(formik.errors.password)
                      }
                    />
                    <PasswordRequirements
                      password={formik.values.password}
                      passwordConfirm={formik.values.passwordConfirm}
                    />
                  </Box>
                  <TextField
                    name="passwordConfirm"
                    type="password"
                    label={polyglot.t('register.confirm_pass')}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.passwordConfirm}
                    required
                    error={
                      formik.touched.passwordConfirm &&
                      Boolean(formik.errors.passwordConfirm)
                    }
                  />
                  {/** terms & conditions */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.termsAndConditions}
                        onChange={(e) => {
                          isRead && formik.handleChange(e);
                        }}
                        value={formik.values.termsAndConditions}
                        defaultChecked={formik.values.termsAndConditions}
                        onBlur={formik.handleBlur}
                        id="termsAndConditions"
                        name="termsAndConditions"
                        onClick={() => !isRead && handleOpenModal()}
                      />
                    }
                    label={
                      <Link onClick={handleOpenModal}>
                        <Typography>
                          {polyglot.t('stoerk_id.request_form.agree_terms')}{' '}
                          <Link onClick={handleOpenModal}>
                            {polyglot.t(
                              'stoerk_id.request_form.agree_terms_link'
                            )}
                          </Link>
                          {polyglot.t('stoerk_id.request_form.agree_terms_end')}
                        </Typography>
                      </Link>
                    }
                  />
                  <TermsAndConditionsDialog
                    setIsRead={setIsRead}
                    open={openModal}
                    closeModal={handleCloseModal}
                    closeModalWithConfirmation={
                      handleCloseModalWithConfirmation
                    }
                    termsAndConditionsTextContent={
                      <Stack>
                        <Document
                          file={'/TERMS_AND_CONDITIONS_OF_SALE.pdf'}
                          onLoadSuccess={({ numPages }) =>
                            setNumPages(numPages)
                          }
                          onError={(...args) => {
                            // eslint-disable-next-line no-console
                            console.log(
                              'Error while loading PDF:',
                              TermsAndConditionsContent,
                              ...args
                            );
                            console.error(args[0]);
                          }}
                          loading={<CircularProgress />}
                          error={
                            <Box sx={{ textAlign: 'center', padding: 2 }}>
                              <Typography color="error">
                                Failed to load PDF. Please try again.
                              </Typography>
                            </Box>
                          }
                        >
                          {Array.from(new Array(numPages), (el, index) => (
                            <Page
                              key={`page_${index + 1}`}
                              pageNumber={index + 1}
                              renderTextLayer={true}
                              renderAnnotationLayer={true}
                              width={750}
                            />
                          ))}
                        </Document>
                      </Stack>
                    }
                  />
                  {formik.touched.termsAndConditions &&
                    formik.errors.termsAndConditions && (
                      <ErrorText>
                        {polyglot.t(formik.errors.termsAndConditions)}
                      </ErrorText>
                    )}
                </>
              ) : null}

              <LoadingButton
                color="primary"
                variant="contained"
                size="large"
                fullWidth
                type="submit"
                loading={isLoading || formik.isSubmitting}
                disabled={isLoading || formik.isSubmitting || !formik.isValid}
              >
                {polyglot.t('register.button_title')}
              </LoadingButton>
            </Stack>
          </form>
        </Box>
      </Box>
    </AuthLayout>
  );
};

const connector = connect(null, {
  registerClient,
});
type ConnectedComponentProps = ConnectedProps<typeof connector>;

export default connector(
  withRouter(withShowErrorMessage(FreshwatchRegisterForm))
);
